/**
 * The motif: an ordered list of brush strokes in domain coordinates (see domain.ts).
 */
import type { DomainPoint } from './domain';

/** Paint roles. The colour a role is shown in depends on the copy's colour class. */
export const ROLE_INK = 0;
export const ROLE_VERMILION = 1;
export const ROLE_GOLD = 2;
export const ROLE_IVORY = 3;
export const ERASER = -1;
export type Role = 0 | 1 | 2 | 3;
export type StrokeRole = Role | typeof ERASER;

export type Symmetry = 'reflection' | 'rotation';

export interface Stroke {
  role: StrokeRole;
  /** The symmetry group the stroke was painted under (it is repeated by that group). */
  symmetry: Symmetry;
  /** Brush diameter as a fraction of the domain scale (the disk radius of vertex R). */
  size: number;
  points: DomainPoint[];
  /** Optional per-point width factors in (0, 1] (pen pressure, tapering). */
  widths?: number[];
}

export interface Motif {
  strokes: Stroke[];
}

export const MIN_BRUSH = 0.02;
export const MAX_BRUSH = 0.42;

export const emptyMotif = (): Motif => ({ strokes: [] });

export function cloneStroke(s: Stroke): Stroke {
  return {
    role: s.role,
    symmetry: s.symmetry,
    size: s.size,
    points: s.points.map((p) => ({ a: p.a, b: p.b })),
    widths: s.widths ? [...s.widths] : undefined,
  };
}

export const cloneMotif = (m: Motif): Motif => ({ strokes: m.strokes.map(cloneStroke) });

/**
 * Ramer–Douglas–Peucker simplification in domain coordinates. Keeps endpoints and every point
 * whose removal would move the polyline by more than `tol`; per-point widths travel along.
 */
export function simplifyStroke(s: Stroke, tol: number): Stroke {
  const pts = s.points;
  if (pts.length <= 2) return cloneStroke(s);
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length > 0) {
    const [i0, i1] = stack.pop()!;
    const A = pts[i0];
    const B = pts[i1];
    const dx = B.a - A.a;
    const dy = B.b - A.b;
    const len2 = dx * dx + dy * dy;
    let worst = -1;
    let worstD = 0;
    for (let i = i0 + 1; i < i1; i++) {
      const P = pts[i];
      let d: number;
      if (len2 === 0) d = Math.hypot(P.a - A.a, P.b - A.b);
      else {
        const t = Math.max(0, Math.min(1, ((P.a - A.a) * dx + (P.b - A.b) * dy) / len2));
        d = Math.hypot(P.a - (A.a + t * dx), P.b - (A.b + t * dy));
      }
      // a width change also counts as a deviation, so tapering survives simplification
      if (s.widths) {
        const t = len2 === 0 ? 0 : ((P.a - A.a) * dx + (P.b - A.b) * dy) / len2;
        const wi = s.widths[i0] + (s.widths[i1] - s.widths[i0]) * Math.max(0, Math.min(1, t));
        d = Math.max(d, Math.abs(s.widths[i] - wi) * s.size * 0.5);
      }
      if (d > worstD) {
        worstD = d;
        worst = i;
      }
    }
    if (worst >= 0 && worstD > tol) {
      keep[worst] = 1;
      stack.push([i0, worst], [worst, i1]);
    }
  }
  const out = cloneStroke(s);
  out.points = pts.filter((_, i) => keep[i]);
  if (s.widths) out.widths = s.widths.filter((_, i) => keep[i]);
  return out;
}
